import { tv } from '../utils/tv';

/**
 * Shared focus ring styles.
 *
 * Draws the focus ring using `outline` directly on the element.
 *
 * ## Slots
 *
 * - **`root`** — single-element pattern. The element has `data-focus-visible`
 *   and renders the ring. Use for buttons, links, and simple controls.
 * - **`group` + `item`** — two-element pattern. An ancestor (`group`) carries
 *   `data-focus-visible`; the descendant (`item`) renders the ring.
 *
 * ## Variants
 *
 * - `color: 'default'` (default) — `--focus-ring-main`
 * - `color: 'static-white'` — `--focus-ring-white`, for dark backgrounds
 * - `position: 'outer'` (default) — ring outside the element with a gap equal to `--focus-ring-offset`
 * - `position: 'inner'` — ring inside the element; use when `overflow: hidden` would clip an outside ring
 *
 * ## Usage
 *
 * ```tsx
 * // Single element
 * <button className={focusStyles.root()} data-focus-visible={isFocusVisible || undefined}>
 *   Clear
 * </button>
 *
 * // Group + item (e.g. Checkbox)
 * <div className={focusStyles.group()} data-focus-visible={isFocusVisible || undefined}>
 *   <span className={focusStyles.item()}>
 *     <Icon />
 *   </span>
 * </div>
 *
 * // Inset ring (e.g. ListItemButton — avoids overflow:hidden clipping)
 * const focusStyles = focusRingStyles({ position: 'inner' });
 * <button className={focusStyles.root()} data-focus-visible={isFocusVisible || undefined}>
 *   ...
 * </button>
 * ```
 */
export const focusRingStyles = tv({
  slots: {
    root: ['focus-ring-width outline-none', 'focus-visible:outline-solid'],
    group: 'group/focus',
    item: [
      'focus-ring-width outline-none',
      'group-focus-visible/focus:outline-solid',
    ],
  },
  variants: {
    color: {
      default: {
        root: 'focus-ring-main',
        item: 'focus-ring-main',
      },
      'static-white': {
        root: 'focus-ring-white',
        item: 'focus-ring-white',
      },
    },
    position: {
      outer: {
        root: 'focus-ring-offset',
        item: 'focus-ring-offset',
      },
      inner: {
        root: '[outline-offset:calc(-1*var(--focus-ring-width))]',
        item: '[outline-offset:calc(-1*var(--focus-ring-width))]',
      },
    },
  },
  defaultVariants: {
    color: 'default',
    position: 'outer',
  },
});
